import {
  createContext,
  FC,
  useCallback,
  useContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";

import capitalize from "lodash/capitalize";
import groupBy from "lodash/groupBy";
import noop from "lodash/noop";
import partition from "lodash/partition";
import sortBy from "lodash/sortBy";

import { useToast } from "@hightouch/htui";

import { useUser } from "src/contexts/user-context";
import {
  DestinationDefinitionFragment as DestinationDefinition,
  MakeOptional,
  Maybe,
  SourceColumnDescription,
  SourceDefinitionFragment as SourceDefinition,
  SyncableColumnFragment as SyncableColumn,
  SyncQuery,
} from "src/graphql";
import { useModelRun, useUpdateQuery } from "src/utils/models";
import { useModelState } from "src/hooks/use-model-state";
import { ExtendedTypes, RelationshipHierarchy } from "@hightouch/formkit";
import {
  isRelatedColumn,
  isTransformedColumn,
  isTraitColumn,
  isMergedColumn,
  isRelatedJourneyEventColumn,
  ColumnType,
  type RelatedColumn,
  type TransformedColumn,
} from "@hightouch/lib/query/visual/types";
import { OverrideConfig } from "src/components/destinations/types";
import { SPLIT_ID_ALIAS } from "@hightouch/lib/query/visual/util";
import { JourneyNodeSyncMode, JourneySyncExitConfig } from "src/types/journeys";
import { NullableProperties } from "src/types/utils";
import { isSyncableJourneyVariableColumn } from "./utils";
import { replacePrefix } from "./utils";
import type { MatchBoosterRegion } from "src/utils/match-booster-regions";

export type ColumnModelType = "model" | "related" | "journey-event";

export type ColumnOption = {
  customType?: string | null;
  description?: string | null;
  extendedType?: ExtendedTypes;
  icon?: React.ElementType;
  label: string;
  /** Different than alias. This is the name that will be populated when the option is selected. */
  suggestedName?: string;
  metadata?: {
    properties: Record<string, string[] | null>;
  };
  entity?: {
    name: string;
    modelType: ColumnModelType | null;
    isMerged: boolean;
    isTrait: boolean;
    isSplit: boolean;
    isJourneyContextVariable: boolean;
  };
  /**
   * For merged columns: the relationship names along the merge path, parallel
   * to `column_reference.path` (root-first). Used to build Liquid template
   * references like `merged['RelA.RelB.column']`.
   */
  mergedRelationshipNames?: string[] | null;
  /**
   * Used to group options into sub-groups within the GroupedCombobox.
   */
  options?: ColumnOption[];
  /**
   * Event field suggestion arrays can include nested element field suggestions.
   * This is only used with event models to scope nested "create an array" mappings.
   */
  elementOptions?: ColumnOption[];
  type: string | null;
  value: string | Record<string, unknown>;
};

export type FormkitSync = SyncQuery["syncs"][number] & {
  segment:
    | (NonNullable<SyncQuery["syncs"][number]>["segment"] & {
        // These fields are used for sync templates.
        // Unlike a regular sync, these fields will be on the segment
        // instead of the segment's parent.
        // In the context of a sync template the segment _is_ the parent model.
        syncable_columns: SyncableColumn[];
        relationships?: Array<{ id: number; name: string | null }>;
      })
    | null;
};

export type FormkitSyncTemplateConfig = {
  id: number | null;
  config: Record<string, unknown> | null;
  override_config: OverrideConfig | null; // The overridable fields
  journey_settings?: {
    // Journey-specific settings that are only used for journeys
    mode?: JourneyNodeSyncMode;
    exit_config?: NullableProperties<JourneySyncExitConfig>;
  } | null;
};

export type FormkitModel = MakeOptional<
  Pick<
    NonNullable<FormkitSync["segment"]>,
    | "columns"
    | "syncable_columns"
    | "connection"
    | "custom_query"
    | "id"
    | "is_realtime_audience"
    | "name"
    | "parent"
    | "primary_key"
    | "relationships"
    | "query_dbt_model_id"
    | "query_looker_look_id"
    | "query_integrations"
    | "query_raw_sql"
    | "query_table_name"
    | "query_type"
    | "visual_query_filter"
    | "visual_query_parent_id"
    | "matchboosting_enabled"
    | "matchbooster_settings"
  >,
  | "custom_query"
  | "is_realtime_audience"
  | "parent"
  | "relationships"
  | "query_dbt_model_id"
  | "query_looker_look_id"
  | "query_integrations"
  | "query_raw_sql"
  | "query_table_name"
  | "visual_query_filter"
  | "visual_query_parent_id"
  | "matchbooster_settings"
> & {
  source_column_descriptions?: Array<SourceColumnDescription> | null;
  /**
   * If true, this "model" is backed by event-schema suggestions (not warehouse columns).
   * Used to tweak mapper UX (e.g. allow "create new field" from search).
   */
  isEventModel?: boolean;
};

// `config` (stripped destination config) is only selected for existing syncs;
// destination-picker and template flows construct a FormkitDestination without
// it, so keep it optional here.
export type FormkitDestination = MakeOptional<
  NonNullable<FormkitSync["destination"]>,
  "config"
>;

export type ExternalSegment = {
  id: string;
  external_id: Maybe<string>;
  created_at: string;
};

export type DraftChange = { key: string; op: "add" | "replace" };

type FormkitSyncTemplateOverrideContext = {
  showLockUI: boolean; // Show lock UI
  showRestoreDefaultUI: boolean; // Show the 'restore default' UI
  overrideConfig: OverrideConfig | null;
  relationshipHierarchy: RelationshipHierarchy | null;
  relatedSyncsWithOverrides: Record<string, number> | null;
  isFieldDisabled: (key: string) => boolean;
  /**
   * Function to add missing properties to the relationship hierarchy
   *
   * Some keys do not exist in the formkit definition, but are important when syncing.
   * Ex: `autoSyncColumns` is not in the google sheets formkit definition, but if it's true then `mappings` will be ignored
   * `autoSyncColumns` is set in the mappings component
   */
  onAddMissingRelationships: (parent: string, keys: string[]) => void;
};

export type BaseFormkitContextType = {
  destination: FormkitDestination | undefined;
  destinationDefinition: DestinationDefinition | undefined;
  sourceDefinition: SourceDefinition | undefined;
  model: FormkitModel | undefined;
  sync: FormkitSync | undefined;
  matchBoosterRegionControl?: ReactNode;
  selectedMatchBoosterRegion?: MatchBoosterRegion;
  /**
   * True when any sync built from the sync template being edited has run.
   * A template never runs on its own, so `sync.last_run_at` is always absent
   * on the template form and cannot pin region or targeting level there.
   */
  hasDependentSyncRun?: boolean;
  syncTemplate: FormkitSyncTemplateConfig | undefined;
  supportsMatchboosting: boolean | undefined;
  slug: string | undefined;
  validate: any;
  isEventForwardingForm?: boolean;
  journeySettings?: {
    mode?: JourneyNodeSyncMode;
    exit_config?: NullableProperties<JourneySyncExitConfig>;
  };
  /**Used for drafts */
  isModelDraft?: boolean;
  draftChanges?: DraftChange[];
};

export type FormkitContextType = BaseFormkitContextType & {
  columns: ColumnOption[];
  sourceId: string | undefined;
  workspaceId: string;
  isSetup: boolean;
  /**
   * Set by extension configuration forms (context-hub integrations) so form
   * references can resolve the saved extension's id (e.g. to fetch dynamic
   * options from the extension's provider). Undefined during first-time setup
   * and for non-extension forms.
   */
  extensionId?: string;
  tunnel: any;
  credentialId: string | undefined;
  externalSegment: ExternalSegment | undefined;
  destinationConfig: Record<string, unknown> | undefined;
  syncConfig: Record<string, unknown> | undefined;
  // Used to set cloud provider from forms
  setCredentialId?: (credentialId: string) => void;
  /**Used for sync template overrides */
  /**
   * Used to determine where the missing relationships should exist in the formkit definition
   */
  missingRelationshipsInFormkitDefinition: Record<string, string[]> | null;
} & FormkitSyncTemplateOverrideContext;

export const FormkitContext = createContext<FormkitContextType>(
  {} as FormkitContextType,
);
FormkitContext.displayName = "FormkitContext";

export const useFormkitContext = () =>
  useContext<FormkitContextType>(FormkitContext);

export type FormkitDataContextType = {
  reloadModel: () => void;
  reloadRows: () => void;
  queryRowsError: string | undefined;
  loadingModel: boolean;
  loadingRows: boolean;
  rows: any;
};

// Volatile warehouse-preview data, kept separate from the stable config context.
// A preview resolving mid-load then re-renders only the components that read it.
export const FormkitDataContext = createContext<FormkitDataContextType>({
  reloadModel: () => {},
  reloadRows: () => {},
  queryRowsError: undefined,
  loadingModel: false,
  loadingRows: false,
  rows: undefined,
});
FormkitDataContext.displayName = "FormkitDataContext";

export const useFormkitDataContext = () =>
  useContext<FormkitDataContextType>(FormkitDataContext);

type FormkitProviderProps = {
  children: ReactNode;
  matchBoosterRegionControl?: ReactNode;
  selectedMatchBoosterRegion?: MatchBoosterRegion;
  /** See {@link BaseFormkitContextType.hasDependentSyncRun}. */
  hasDependentSyncRun?: boolean;
  model?: FormkitModel;
  destination?: FormkitDestination;
  destinationConfig?: Record<string, unknown>;
  sync?: FormkitSync;
  syncTemplate?: FormkitSyncTemplateConfig;
  syncConfig?: Record<string, unknown>;
  relatedSyncsWithOverrides?: Record<string, number>;
  destinationDefinition?: DestinationDefinition;
  sourceDefinition?: SourceDefinition;
  externalSegment?: ExternalSegment;
  supportsMatchboosting?: boolean;
  validate: any;
  sourceId?: string;
  credentialId?: string;
  setCredentialId?: (credentialId: string) => void;
  isFieldDisabled?: (key: string) => boolean;
  journeySettings?: {
    mode?: JourneyNodeSyncMode;
    exit_config?: NullableProperties<JourneySyncExitConfig>;
  };
  /**Used within formkit to determine how to render secret fields */
  isSetup?: boolean;
  tunnel?: any;
  /**Used for drafts */
  isModelDraft?: boolean;
  draftChanges?: DraftChange[];
  isEventForwardingForm?: boolean;
  /**
   * Optional override for the source columns shown throughout formkit (mapper, ColumnOrConstant, Column, etc).
   * Used for event forwarding to surface event property suggestions.
   */
  columns?: ColumnOption[];
  /**
   * Override for refresh buttons and loading states for model columns/schema.
   */
  reloadModelOverride?: () => void;
  loadingModelOverride?: boolean;
  /**
   * Override for refresh buttons and loading states for row data.
   */
  reloadRowsOverride?: () => void;
  loadingRowsOverride?: boolean;
  /**Used for sync template overrides */
} & Partial<FormkitSyncTemplateOverrideContext>;

const getColumnValue = (column: any) => {
  if (column.column_reference) {
    if (column.column_reference.type === "raw") {
      return column.column_reference.name;
    } else {
      return column.column_reference;
    }
  }
  return column.name;
};

const getModelType = (
  column: SyncableColumn,
  topLevelModelId: string,
): ColumnModelType => {
  if (isMergedColumn(column.column_reference)) {
    return "related";
  }

  if (isTrait(column)) {
    // Event model columns cannot be synced unless they are used in a journey
    if (column.model.is_event_model) return "journey-event";

    return column.model.model_id?.toString() === topLevelModelId.toString()
      ? "model"
      : "related";
  }

  if (column.is_boosted_column) {
    return "model";
  }

  // Event model columns cannot be synced unless they are used in a journey
  return column.model.is_event_model ? "journey-event" : "model";
};

/**
 * Maps a SyncableColumn to a ColumnOption for UI display
 */
const mapColumn = ({
  column,
  isModelDraft,
  modelId,
  description,
}: {
  column: SyncableColumn;
  isModelDraft: boolean | undefined;
  modelId: string;
  description?: string | null;
}): ColumnOption => {
  const isMerged = isMergedColumn(column.column_reference);
  const isSplitColumn = isSplit(column);
  const isTraitColumn = isTrait(column);
  const isJourneyContextVariableColumn =
    isSyncableJourneyVariableColumn(column);

  return {
    customType: column.custom_type,
    label: column.alias || column.name,
    suggestedName: isJourneyContextVariableColumn
      ? replacePrefix(
          column.alias || column.name,
          JOURNEY_CONTEXT_VARIABLE_VALUE_PREFIX,
        )
      : undefined,
    metadata: column.metadata,
    entity: {
      name: !isSplitColumn
        ? column.model.model_name + (isModelDraft ? " (draft)" : "")
        : column.model.model_name,
      modelType: getModelType(column, modelId),
      isMerged,
      isSplit: isSplitColumn,
      isTrait: isTraitColumn,
      isJourneyContextVariable: isJourneyContextVariableColumn,
    },
    type: isSplitColumn ? ColumnType.String : column.type,
    value: getColumnValue(column),
    mergedRelationshipNames: column.merged_relationship_names,
    description,
  };
};

export const MATCHBOOSTED_IDENTIFIER_KEY = "__BOOSTED_COLUMNS__";
export const TRAIT_IDENTIFIER_KEY = "__TRAITS__";
export const SPLIT_IDENTIFIER_KEY = "__SPLITS__";
export const JOURNEY_CONTEXT_VARIABLE_IDENTIFIER_KEY =
  "__JOURNEY_CONTEXT_VARIABLES__";
export const JOURNEY_CONTEXT_VARIABLE_VALUE_PREFIX = "_ht_context_col_";

function isSplit(column: SyncableColumn | undefined): boolean {
  return column?.column_reference?.type === "splitTest";
}

// Covers parent-level and audience-level traits
function isTrait(column: SyncableColumn | undefined): boolean {
  const isParentTrait =
    (isRelatedColumn(column?.column_reference) &&
      isTraitColumn(column?.column_reference.column)) ||
    // Formula trait is represented as a TransformedColumn
    isTransformedColumn(column?.column_reference);

  const isAudienceTrait =
    column?.column_reference?.type === "additionalColumnReference";

  return isParentTrait || isAudienceTrait;
}

/**
 * Groups and maps columns into categorized options for UI display
 *
 * Columns are grouped into:
 * 1. Boosted columns (if supported)
 * 2. Trait columns
 * 3. Split columns
 * 4. Journey context variable columns
 * 5. Regular columns (grouped by model name). This includes the model that's being synced and columns from merged models.
 */
// Module-scope default so callers that omit `isFieldDisabled` don't allocate a
// fresh arrow each render — would otherwise destabilize the value memo for
// every caller that doesn't override the prop.
const DEFAULT_IS_FIELD_DISABLED = (): boolean => false;

export const mapColumns = ({
  columns,
  isModelDraft,
  modelId,
  sourceColumnDescriptions,
}: {
  columns: SyncableColumn[] | undefined;
  isModelDraft: boolean | undefined;
  modelId: string;
  sourceColumnDescriptions?: Array<SourceColumnDescription> | null;
}): {
  label: string;
  options: ColumnOption[];
  type: string | null;
  value: string;
}[] => {
  // Early return if no columns
  if (!columns?.length) {
    return [];
  }

  // Create description lookup map
  const descriptionMap = createDescriptionMap(sourceColumnDescriptions);

  // Categorize columns by type
  // Note that we want to prevent users selecting MB columns because these should only
  // be appended through the MB toggle in the mapper
  const columnCategories = categorizeColumns(columns);
  const {
    journeyContextVariableColumns,
    traitColumns,
    splitColumns,
    regularColumns,
  } = columnCategories;

  // Group regular columns by model name
  const modelGroups = groupBy(
    regularColumns,
    (column) => column.model.model_name,
  );

  // Combine all groups
  const groups = {
    ...modelGroups,
    ...(traitColumns.length > 0 && { [TRAIT_IDENTIFIER_KEY]: traitColumns }),
    ...(splitColumns.length > 0 && { [SPLIT_IDENTIFIER_KEY]: splitColumns }),
    ...(journeyContextVariableColumns.length > 0 && {
      [JOURNEY_CONTEXT_VARIABLE_IDENTIFIER_KEY]:
        journeyContextVariableColumns.map((column) => ({
          ...column,
          // This is used to display the column name in the UI on the left hand side of the mapper.
          // Remove the prefix from the column name that's prepended in the backend
          alias: column.alias
            ? replacePrefix(column.alias, JOURNEY_CONTEXT_VARIABLE_VALUE_PREFIX)
            : null,
        })),
    }),
  };

  // Format groups into the expected output structure
  return formatGroupsToColumnOptions(groups, {
    isModelDraft,
    modelId,
    descriptionMap,
  });
};

/**
 * Creates a lookup map of column descriptions by name
 */
function createDescriptionMap(
  sourceColumnDescriptions?: Array<SourceColumnDescription> | null,
): Record<string, string | null> {
  return (
    sourceColumnDescriptions?.reduce<Record<string, string | null>>(
      (result, { name, description }) => {
        if (name && description) {
          result[name.toLowerCase()] = description;
        }
        return result;
      },
      {},
    ) ?? {}
  );
}

/**
 * Categorizes columns into boosted, trait, split, and regular columns
 */
function categorizeColumns(columns: SyncableColumn[]) {
  const [boostedColumns, nonBoostedColumns] = partition(
    columns,
    (column) => column.is_boosted_column,
  );

  const [traitColumns, nonTraitColumns] = partition(
    nonBoostedColumns,
    (column) => isTrait(column),
  );

  const [splitColumns, regularColumns] = partition(nonTraitColumns, (column) =>
    isSplit(column),
  );

  const [journeyContextVariableColumns, nonJourneyContextVariableColumns] =
    partition(regularColumns, (column) =>
      isSyncableJourneyVariableColumn(column),
    );

  return {
    boostedColumns,
    traitColumns,
    splitColumns,
    journeyContextVariableColumns,
    regularColumns: nonJourneyContextVariableColumns,
  };
}

/**
 * Formats grouped columns into the expected column options structure
 */
function formatGroupsToColumnOptions(
  groups: Record<string, SyncableColumn[]>,
  options: {
    isModelDraft: boolean | undefined;
    modelId: string;
    descriptionMap: Record<string, string | null>;
  },
) {
  const { isModelDraft, modelId, descriptionMap } = options;

  const formattedGroups = Object.entries(groups).map(
    ([key, columns], index) => {
      const firstColumn = columns[0];

      // First group is always "Columns"
      let label =
        index === 0
          ? "Columns"
          : capitalize(key.replace(/_/g, " ").trim().toLowerCase());
      const isMergedModel = isMergedColumn(firstColumn?.column_reference);
      const isJourneyEventModel = isRelatedJourneyEventColumn(
        firstColumn?.column_reference,
      );

      if (isMergedModel) {
        label = "Merged: " + label;
      } else if (isJourneyEventModel) {
        label = "Properties of journey entry event";
      }

      return {
        label,
        options: sortBy(
          columns.map((column) =>
            mapColumn({
              column,
              isModelDraft,
              modelId,
              description: descriptionMap[column.name.toLowerCase()],
            }),
          ),
          ["label"],
        ),
        entity: {
          name: key,
          modelType: firstColumn ? getModelType(firstColumn, modelId) : null,
          isMerged: isMergedModel,
          isSplit: isSplit(firstColumn),
          isTrait: isTrait(firstColumn),
          isJourneyContextVariable:
            isSyncableJourneyVariableColumn(firstColumn),
        },
        type: firstColumn ? getModelType(firstColumn, modelId) : "",
        value: "",
      } satisfies ColumnOption;
    },
  );

  // Filter out empty groups
  return formattedGroups.filter((group) => group.options.length > 0);
}

function extractTraitDefinitionIds(obj: unknown): Set<string> {
  const ids = new Set<string>();
  if (typeof obj !== "object" || obj === null) return ids;
  if (Array.isArray(obj)) {
    for (const item of obj) {
      for (const id of extractTraitDefinitionIds(item)) ids.add(id);
    }
  } else {
    for (const [key, value] of Object.entries(obj)) {
      if (key === "traitDefinitionId" && typeof value === "string") {
        ids.add(value);
      } else {
        for (const id of extractTraitDefinitionIds(value)) ids.add(id);
      }
    }
  }
  return ids;
}

export const FormkitProvider: FC<Readonly<FormkitProviderProps>> = ({
  children,
  matchBoosterRegionControl,
  selectedMatchBoosterRegion,
  hasDependentSyncRun,
  model,
  destination,
  destinationDefinition,
  sourceDefinition,
  supportsMatchboosting,
  showLockUI = false,
  sync,
  syncTemplate,
  relatedSyncsWithOverrides = null,
  syncConfig,
  externalSegment,
  validate,
  sourceId,
  isSetup,
  tunnel,
  credentialId,
  setCredentialId,
  draftChanges,
  isModelDraft,
  destinationConfig,
  journeySettings,
  relationshipHierarchy = null,
  showRestoreDefaultUI = false,
  isFieldDisabled = DEFAULT_IS_FIELD_DISABLED,
  onAddMissingRelationships = noop,
  isEventForwardingForm = false,
  columns: columnsOverride,
  reloadModelOverride,
  loadingModelOverride,
  reloadRowsOverride,
  loadingRowsOverride,
}) => {
  const { workspace } = useUser();
  const update = useUpdateQuery();
  const { toast } = useToast();

  // XXX: Used to determine where the missing relationships should exist in the formkit definition
  const [
    missingRelationshipsInFormkitDefinition,
    setMissingRelationshipsInFormkitDefinition,
  ] = useState<Record<string, string[]>>({});

  const addMissingRelationships = useCallback(
    (parent: string, keys: string[]) => {
      setMissingRelationshipsInFormkitDefinition((previous) => ({
        ...previous,
        [parent]: keys,
      }));
      onAddMissingRelationships(parent, keys);
    },
    [onAddMissingRelationships],
  );

  const isSyncTemplate = syncTemplate != null;
  const syncableColumns: SyncableColumn[] | undefined = useMemo(() => {
    const columns = model?.syncable_columns;

    // If this is sync template, we'll add a synthetic split column.
    // It's synthetic because the sync template is connected to a parent model
    // and parent models don't have splits.
    // If this column is used in a mapping, at sync time we'll replace this it with the audience's
    // actual split column. If the audience doesn't have a split column, we'll sync a null value.
    if (isSyncTemplate) {
      columns?.push({
        alias: null,
        // The backend doesn't care what the `columnAlias` is set to
        // because it will be replaced with the audience's split column name
        // at sync time. It's only used as a label in the UI.
        column_reference: { type: "splitTest", columnAlias: "Split column" },
        custom_type: null,
        effective_privacy_level: null,
        top_k_enabled: null,
        is_boosted_column: false,
        merged_relationship_names: null,
        metadata: null,
        model: {
          model_id: model?.id,
          model_name: model?.name ?? "",
          is_event_model: false,
        },
        name: "Split column",
        type: null,
      });
      // Push a second column representing our Hightouch Split UUID (this is audience_splits.id in Hightouch DB)
      columns?.push({
        alias: null,
        column_reference: { type: "splitTest", columnAlias: SPLIT_ID_ALIAS },
        custom_type: null,
        effective_privacy_level: null,
        top_k_enabled: null,
        is_boosted_column: false,
        merged_relationship_names: null,
        metadata: null,
        model: {
          model_id: model?.id,
          model_name: model?.name ?? "",
          is_event_model: false,
        },
        name: "Split ID",
        type: null,
      });
    }

    return columns;
  }, [isSyncTemplate, model?.syncable_columns, model?.id, model?.name]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const columns = useMemo<ColumnOption[]>(() => {
    if (columnsOverride) {
      return columnsOverride;
    }
    return mapColumns({
      columns: syncableColumns,
      isModelDraft,
      modelId: model?.parent ? model.parent.id : model?.id,
      sourceColumnDescriptions: model?.source_column_descriptions,
    });
  }, [
    columnsOverride,
    syncableColumns,
    isModelDraft,
    model?.parent?.id,
    model?.id,
    model?.source_column_descriptions,
  ]);

  const modelState = useModelState(model);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (model) {
      modelState.set(model);
    }
  }, [model]);

  const modelStateWithTraits = useMemo(() => {
    const effectiveConfig = syncConfig ?? sync?.config;
    const syncedTraitIds = extractTraitDefinitionIds(effectiveConfig);

    const traitAdditionalColumns = (model?.syncable_columns ?? [])
      .filter(
        (column) =>
          column.alias != null &&
          ((isRelatedColumn(column.column_reference) &&
            isTraitColumn(column.column_reference.column)) ||
            isTransformedColumn(column.column_reference)),
      )
      .map((column) => ({
        alias: column.alias!,
        column: column.column_reference as RelatedColumn | TransformedColumn,
      }))
      .filter((col) => {
        if (isRelatedColumn(col.column) && isTraitColumn(col.column.column)) {
          return syncedTraitIds.has(col.column.column.traitDefinitionId);
        }
        if (
          isTransformedColumn(col.column) &&
          col.column.column.type === "trait"
        ) {
          return syncedTraitIds.has(col.column.column.traitDefinitionId);
        }
        return false;
      });

    if (!traitAdditionalColumns.length) {
      return modelState.state;
    }

    return {
      ...modelState.state,
      visual_query_filter: {
        conditions: [],
        ...modelState.state.visual_query_filter,
        additionalColumns: [
          ...(modelState.state.visual_query_filter?.additionalColumns ?? []),
          ...traitAdditionalColumns,
        ],
      },
    };
  }, [modelState.state, model?.syncable_columns, syncConfig, sync?.config]);

  // The preview query is also used for the "Test a row" flow. Forward the
  // sync configuration so the backend can enrich the query with additional
  // columns referenced only by template mappings (e.g. `merged['Rel.COL']`
  // inside a webhook body). Without this, the preview row is missing those
  // columns and the template lookup renders them as empty strings.
  const previewSyncConfig = useMemo(
    () => syncConfig ?? sync?.config ?? undefined,
    [syncConfig, sync?.config],
  );

  // Build the column list for the preview / "Test a row" panel. The base
  // columns cover the parent model, but merged columns (those whose
  // column_reference is a merged/related reference) live only in
  // syncable_columns and are never in model.columns, so they must be appended
  // here. Without this, merged+redacted columns are invisible to aliasRow and
  // silently disappear from the panel.
  const previewColumns = useMemo(() => {
    const mergedColumns = (model?.syncable_columns ?? [])
      .filter((col) => isMergedColumn(col.column_reference))
      .map((col) => ({
        name: `${col.model.model_name}:${col.name}`,
        alias: col.alias,
        disable_preview: false,
        redacted_text: null as string | null,
        privacy_level: col.effective_privacy_level,
      }));

    if (!mergedColumns.length) return undefined;
    return [...(model?.columns ?? []), ...mergedColumns];
  }, [model?.columns, model?.syncable_columns]);

  const {
    rows,
    error: queryRowsError,
    runQuery,
    getSchema,
    schemaLoading,
    loading: rowsLoading,
  } = useModelRun(modelStateWithTraits, {
    columns: previewColumns,
    syncConfig: previewSyncConfig as Record<string, unknown> | undefined,
    onCompleted: async ({ columns, rows }, error) => {
      if (error || !columns) {
        return;
      }

      if (rows && columns) {
        const jsonColumnsMetadata = extractJsonColumnsAndTheirProperties(rows);

        if (jsonColumnsMetadata) {
          const keys = Object.keys(jsonColumnsMetadata);
          keys.forEach((key) => {
            columns.forEach((column, index) => {
              if (column.name === key) {
                // @ts-expect-error upgraded type from `any`, metadata should be typed
                columns[index].metadata = {
                  properties: jsonColumnsMetadata[key]
                    ? Array.from(jsonColumnsMetadata[key] || [])
                    : undefined,
                };
              }
            });
          });
        }
      }

      try {
        await update({
          model: modelState.state,
          columns,
          overwriteMetadata: rows ? true : false,
        });
      } catch {
        toast({
          id: "save-model-after-query",
          title: "There was an issue saving the model",
          variant: "error",
        });
      }
    },
  });

  const columnsLoading = sourceDefinition?.supportsResultSchema
    ? schemaLoading
    : rowsLoading;

  // `runQuery` is `useCallback`-wrapped in `useModelRun`, but its deps include
  // the `options` arg — which is a fresh object literal at our call site each
  // render — so `runQuery` itself is fresh every render. `getSchema` is not
  // `useCallback`-wrapped at all in `usePreviewQuerySchema`. Refs let us read
  // the latest values from inside stable callbacks without dragging the
  // upstream instability into our `useCallback` deps.
  const runQueryRef = useRef(runQuery);
  runQueryRef.current = runQuery;
  const getSchemaRef = useRef(getSchema);
  getSchemaRef.current = getSchema;

  const reloadRows = useCallback(() => {
    return runQueryRef.current({
      limit: true,
      // No reason to add this here since we only care about the columns.
      disableRowCounter: true,
    });
  }, []);

  const reloadColumns = useCallback(() => {
    if (sourceDefinition?.supportsResultSchema) {
      getSchemaRef.current();
    } else {
      reloadRows();
    }
  }, [sourceDefinition?.supportsResultSchema, reloadRows]);

  const contextValue = useMemo<FormkitContextType>(
    () => ({
      isSetup: Boolean(isSetup),
      model,
      destination,
      validate,
      sync,
      matchBoosterRegionControl,
      selectedMatchBoosterRegion,
      hasDependentSyncRun,
      syncTemplate,
      syncConfig: syncConfig ?? sync?.config,
      destinationDefinition,
      destinationConfig,
      sourceDefinition,
      externalSegment,
      columns,
      supportsMatchboosting,
      slug: destination?.type,
      sourceId,
      tunnel,
      workspaceId: String(workspace?.id),
      credentialId,
      setCredentialId,
      draftChanges,
      isModelDraft,
      journeySettings,

      showLockUI,
      overrideConfig:
        // syncTemplate or sync will be passed in, provided by the view. This context is used for both cases.
        // if syncTemplate is passed in, it will be the override config for the sync template
        // if sync is passed in, it will be the override config for the sync template attached to the sync
        syncTemplate?.override_config || sync?.sync_template?.override_config,
      relatedSyncsWithOverrides,
      relationshipHierarchy,
      showRestoreDefaultUI,
      isFieldDisabled,
      missingRelationshipsInFormkitDefinition,
      onAddMissingRelationships: addMissingRelationships,
      isEventForwardingForm,
    }),
    [
      isSetup,
      model,
      destination,
      validate,
      sync,
      matchBoosterRegionControl,
      selectedMatchBoosterRegion,
      hasDependentSyncRun,
      syncTemplate,
      syncConfig,
      destinationDefinition,
      destinationConfig,
      sourceDefinition,
      externalSegment,
      columns,
      supportsMatchboosting,
      sourceId,
      tunnel,
      workspace?.id,
      credentialId,
      setCredentialId,
      draftChanges,
      isModelDraft,
      journeySettings,
      showLockUI,
      relatedSyncsWithOverrides,
      relationshipHierarchy,
      showRestoreDefaultUI,
      // `isFieldDisabled` is a prop with a stable module-scope default
      // (`DEFAULT_IS_FIELD_DISABLED`), so callers that omit it don't churn this
      // dep. Callers that explicitly pass an inline arrow will still
      // destabilize the memo — caller-side stabilization is theirs to fix.
      isFieldDisabled,
      missingRelationshipsInFormkitDefinition,
      addMissingRelationships,
      isEventForwardingForm,
    ],
  );

  const dataContextValue = useMemo<FormkitDataContextType>(
    () => ({
      loadingModel: loadingModelOverride ?? columnsLoading,
      loadingRows: loadingRowsOverride ?? rowsLoading,
      reloadModel: reloadModelOverride ?? reloadColumns,
      reloadRows: reloadRowsOverride ?? reloadRows,
      queryRowsError,
      rows,
    }),
    [
      loadingModelOverride,
      columnsLoading,
      loadingRowsOverride,
      rowsLoading,
      reloadModelOverride,
      reloadColumns,
      reloadRowsOverride,
      reloadRows,
      queryRowsError,
      rows,
    ],
  );

  return (
    <FormkitContext.Provider value={contextValue}>
      <FormkitDataContext.Provider value={dataContextValue}>
        {children}
      </FormkitDataContext.Provider>
    </FormkitContext.Provider>
  );
};

function extractJsonColumnsAndTheirProperties(rows: Record<string, any>[]): {
  [column: string]: Set<string>;
} {
  const jsonColumns: { [column: string]: Set<string> } = {};
  rows.forEach((row) => {
    const columns = Object.keys(row);
    columns.forEach((column) => {
      if (Array.isArray(row[column])) {
        if (!jsonColumns[column]) {
          jsonColumns[column] = new Set();
        }
        const firstEntry = row[column][0];
        if (
          typeof firstEntry === "object" &&
          !Array.isArray(firstEntry) &&
          firstEntry !== null
        ) {
          const props = Object.keys(firstEntry);
          props.forEach((prop) => jsonColumns[column]?.add(prop));
        }
      }
    });
  });

  return jsonColumns;
}
